import { useContext, useEffect } from 'react'
import { AuthContext } from '@/context/auth-context'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { CircularProgressContinuous } from '@/components/loaders/CircularProgressContinuous'
import CreateOrganizationCard from '@/organizations/views/create-organization/CreateOrganizationCard'
import { AccountActions } from '../store/slice'
import SubscriptionDetails from './SubscriptionDetails'
import { Header } from '@/components/header/Header'

/**
 * Account Page
 *
 * Displays subscriptions details and provides a way for users to manage
 * their subscription via the stripe portal and create an organization.
 * - Only single users not part of an organization will have access to this page
 * - Users that are part of an orgnization will have access to the admin dashboard instead (if they are an org admin)
 */
const AccountPage = () => {
  const dispatch = useAppDispatch()

  // Auth context
  const { userAccountData } = useContext(AuthContext)
  const canViewSubscriptionInfo = userAccountData?.canViewStripeSubscriptionInfo

  // Redux state
  const isLoading = useAppSelector((state: RootState) => state.accountState.loading)
  const isInitialLoadComplete = useAppSelector((state: RootState) => state.accountState.initialLoadComplete)

  // Only show the loader during initial fetch. For automatic background refreshes of the account data,
  // we avoid showing the loader to prevent unexpected loading states when data is already visible.
  const showLoader = isLoading && !isInitialLoadComplete

  // Fetch the data required for child components (SubscriptionDetails and CreateOrganizationCard)
  useEffect(() => {
    if (canViewSubscriptionInfo) {
      dispatch(AccountActions.refreshPaymentDetails({ forceRefresh: true }))
    }
  }, [userAccountData, canViewSubscriptionInfo, dispatch])

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <Header title="Manage Subscription" />

      {/* Account Page Content */}
      {showLoader ? (
        <div className="flex-grow flex items-center justify-center min-h-[60vh] px-4">
          <div className="flex flex-col items-center justify-center">
            <CircularProgressContinuous />
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex w-full p-4 lg:px-6 lg:py-8 justify-center">
            <div className="max-w-[944px]">
              {/* Subscription Details Section */}
              <SubscriptionDetails />

              {/* Create Organization Section */}
              <CreateOrganizationCard />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AccountPage
